import { StrictMode, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ToastProvider, setOrganizationTimezone } from '@platform/ui-kit'
import { AuthProvider } from './app/providers/AuthProvider'
import { ErrorBoundary } from './app/errors/ErrorBoundary'
import { router } from './app/routes/router'
import { api, ApiError } from './lib/api'
import './index.css'
import { registerServiceWorker } from '@/lib/push'
import { initObservability } from '@/lib/observability'
import { applyChatAppIdentity } from '@/features/chat/chatApp'
import { BootScreen } from '@/app/shell/BootScreen'

/* گزارش خطا — پیش از هر چیز دیگر، وگرنه خطای خودِ راه‌اندازی گزارش
   نمی‌شود. بدون VITE_SENTRY_DSN بی‌اثر است. */
initObservability()
applyChatAppIdentity()

/* لایه شبیه‌ساز API — پیش از نخستین رندر نصب می‌شود تا هیچ درخواستی
   از آن جلو نزند. در Production این شاخه اصلاً اجرا نمی‌شود. */
if (import.meta.env.DEV && import.meta.env.VITE_MOCK_API === '1') {
  const { installDevMock } = await import('./lib/devMock')
  installDevMock(api)
}

/* ------------------------------------------------------------------
   نقطه ورود — سند مادر بند ۴ (Experience Layer)
   ------------------------------------------------------------------ */

// Timezone سازمان — در فاز ۲ از Settings هسته خوانده می‌شود
setOrganizationTimezone('Asia/Tehran')

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // تلاش مجدد روی خطای مجوز یا اعتبارسنجی بی‌معنی است
        if (error instanceof ApiError) {
          if (error.isUnauthenticated || error.isForbidden || error.isValidation) return false
          if (error.status >= 400 && error.status < 500) return false
        }
        return failureCount < 2
      },
    },
    mutations: {
      // Command حساس باید Idempotency-Key داشته باشد؛ تلاش خودکار
      // بدون آن می‌تواند عملیات تکراری بسازد (بند ۱۵)
      retry: false,
    },
  },
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <AuthProvider>
            {/* همان نشانِ index.html تا رسیدنِ صفحهٔ اول — BootScreen.tsx */}
            <Suspense fallback={<BootScreen />}>
              <RouterProvider router={router} />
            </Suspense>
          </AuthProvider>
        </ToastProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
)

/* ------------------------------------------------------------------
   ثبت Service Worker — پس از بارگذاری، نه حین آن.

   ⚠️ ثبت در مسیر بحرانیِ راه‌اندازی، نخستین رنگ‌آمیزی صفحه را عقب
   می‌اندازد. `load` یعنی کاربر صفحه را دیده و بعد این اتفاق می‌افتد.

   🔴 شکستش بی‌صداست: مرورگری که پشتیبانی نمی‌کند یا بستر ناامن،
   نباید برنامه را بشکند.
   ------------------------------------------------------------------ */
if (import.meta.env.PROD) {
  window.addEventListener('load', () => {
    void registerServiceWorker()
  })
}
